// See EnrollHostsDrawer.spec.md for EHD-FR-001, EHD-FR-009
import { makeStyles } from '@material-ui/styles'
import Badge from 'core/elements/badge/Badge'
import Text from 'core/elements/Text'
import type Theme from 'core/themes/model'
import type { EnrollmentSelection } from './model'

interface Props {
  hostsCount: number
  selection: EnrollmentSelection
}

// Counts the roles that show in the summary line: hypervisor, image library,
// remote support and dns. Storage backends get their own count, so they are left out.
const countRoles = (selection: EnrollmentSelection): number =>
  [selection.hypervisor, selection.imageLibrary, selection.remoteSupport, selection.dns].filter(
    Boolean,
  ).length

// Label and value pairs for the header summary, with a dash for anything not picked yet
export const buildSummaryItems = (selection: EnrollmentSelection): Array<[string, string]> => {
  const roles = countRoles(selection)
  return [
    ['Cluster', selection.clusterName || '-'],
    ['Network', selection.hostConfigName || '-'],
    ['Volume backends', selection.backends.length > 0 ? String(selection.backends.length) : '-'],
    ['Roles', roles > 0 ? String(roles) : '-'],
  ]
}

// Plain text form of the summary ("Cluster: x | Network: y | ..."). Screen readers get this,
// since the dividers between items are only visual.
export const buildSummaryText = (selection: EnrollmentSelection): string =>
  buildSummaryItems(selection)
    .map(([label, value]) => `${label}: ${value}`)
    .join(' | ')

// EHD-FR-009: Enroll Hosts ships as Beta, same badge as the Beta cards in CardButton
export const BetaBadge = () => {
  const classes = useStyles()
  return <Badge className={classes.betaBadge} variant="primary" text="Beta" />
}

// "Enroll Hosts" with the Beta badge, used by the batch action on the hosts list and details pages
export const EnrollHostsLabel = () => {
  const classes = useStyles()
  return (
    <span className={classes.withBadge}>
      Enroll Hosts <BetaBadge />
    </span>
  )
}

// Drawer title used as the Modal's title prop: "Enroll Hosts (N hosts)" plus the live summary.
// Grey label with bold value follows the Stretched Cluster overview fields.
export default function EnrollmentHeader({ hostsCount, selection }: Props) {
  const classes = useStyles()
  const hostWord = hostsCount === 1 ? 'host' : 'hosts'
  return (
    <div>
      <Text variant="subtitle1" className={classes.title}>
        Enroll Hosts{' '}
        <span className={classes.hostCount}>
          ({hostsCount} {hostWord})
        </span>
        <BetaBadge />
      </Text>
      <fieldset className={classes.summary} aria-label={buildSummaryText(selection)}>
        {buildSummaryItems(selection).map(([label, value]) => (
          <div key={label} className={classes.summaryItem}>
            <Text variant="body2" component="span" className={classes.summaryLabel}>
              {label}:
            </Text>
            <Text variant="caption1" component="span">
              {value}
            </Text>
          </div>
        ))}
      </fieldset>
    </div>
  )
}

const useStyles = makeStyles<Theme>((theme) => ({
  // Badge renders a block div, so the title is a flex row to keep it on the same line
  title: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  withBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  // Title stays the drawer title size; the count is quieter so the header reads lighter
  hostCount: {
    fontSize: 16,
    fontWeight: 'normal',
    color: theme.palette.text.secondary,
  },
  // fieldset groups the items for screen readers; its browser border and padding are reset
  summary: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    border: 0,
    padding: 0,
    margin: theme.spacing(1, 0, 0),
    minWidth: 0,
  },
  summaryItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(0, 2),
    '&:first-child': {
      paddingLeft: 0,
    },
    '& + &': {
      borderLeft: `1px solid ${theme.components.card.border}`,
    },
  },
  summaryLabel: {
    color: theme.palette.text.secondary,
  },
  betaBadge: {
    display: 'inline',
    padding: theme.spacing(0.5, 1),
    fontSize: 12,
  },
}))
